import { preferences } from '@apexx/storage';
import { getAccessibleHubAppIds, type HubAppId } from '@apexx/shell-contract';

export const LAST_HUB_APP_PREFS_KEY = 'apexx.hub.lastAppId';

export async function getLastHubAppId(): Promise<string | null> {
	try {
		const value = await preferences.getItem(LAST_HUB_APP_PREFS_KEY);
		const trimmed = value?.trim();
		return trimmed && trimmed.length > 0 ? trimmed : null;
	} catch {
		return null;
	}
}

export async function setLastHubAppId(appId: string): Promise<void> {
	const trimmed = appId.trim();
	if (!trimmed) {
		return;
	}

	try {
		await preferences.setItem(LAST_HUB_APP_PREFS_KEY, trimmed);
	} catch {
		// Ignore quota / private-mode failures; next launch simply won't restore.
	}
}

/** Clears the last hub app so post-auth does not bounce back into a failing app. */
export async function clearLastHubAppId(): Promise<void> {
	try {
		await preferences.removeItem(LAST_HUB_APP_PREFS_KEY);
	} catch {
		// Ignore storage failures.
	}
}

/**
 * Returns the persisted last hub app id when it is still accessible for this token.
 */
export async function resolveLastAccessibleHubAppId(
	accessToken: string | undefined,
): Promise<HubAppId | null> {
	const lastAppId = await getLastHubAppId();
	if (!lastAppId) {
		return null;
	}

	const accessible = getAccessibleHubAppIds(accessToken);
	if (!accessible.includes(lastAppId as HubAppId)) {
		return null;
	}

	return lastAppId as HubAppId;
}
